import React, { useMemo } from 'react';
import { Badge, Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { DataTable } from '../../../../../../components/datatable';
import { IDifalNota } from '../../../../../../types/icms';
import { formatCPFOrCNPJ, formatCurrency, toNumber } from '../../../../../../utils/FormatFunctions';
import { IPagination } from '../../../../../../types/shared';

interface DifalNotasTableProps {
  data: IDifalNota[];
  pagination: IPagination;
  isFetching?: boolean;
}

const formatPercent = (v: number | string | undefined | null): string => {
  const n = toNumber(v);
  return `${(n * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const isPessoaFisica = (nota: IDifalNota): boolean => {
  if (nota.tins_clifor !== undefined) return nota.tins_clifor === 2;
  const digits = (nota.cgce_clifor ?? '').replace(/\D/g, '');
  return digits.length > 0 && digits.length <= 11;
};

function AuditTooltip({ nota }: { nota: IDifalNota }) {
  const aliqInter = toNumber(nota.aliquota_interestadual);
  const aliqInterna = toNumber(nota.aliquota_interna);
  const vprodutos = toNumber(nota.vprodutos ?? nota.vcontabil);
  const valorDifal = toNumber(nota.valor_difal_calculado);

  if (nota.formula_aplicada === 'base_dupla') {
    const icmsOrigem = vprodutos * aliqInter;
    const semIcms = vprodutos - icmsOrigem;
    const denominador = 1 - aliqInterna;
    const bcDupla = denominador !== 0 ? semIcms / denominador : 0;
    const icmsDestino = bcDupla * aliqInterna;
    return (
      <Box fontSize="11px" lineHeight="1.6">
        <Text fontWeight="bold" mb={1}>
          Base dupla
        </Text>
        <Text>
          1) ICMS Origem = {formatCurrency(vprodutos)} × {formatPercent(aliqInter)} ={' '}
          {formatCurrency(icmsOrigem)}
        </Text>
        <Text>
          2) Sem ICMS = {formatCurrency(vprodutos)} − {formatCurrency(icmsOrigem)} ={' '}
          {formatCurrency(semIcms)}
        </Text>
        <Text>
          3) BC dupla = {formatCurrency(semIcms)} / (1 − {formatPercent(aliqInterna)}) ={' '}
          {formatCurrency(bcDupla)}
        </Text>
        <Text>
          4) ICMS Destino = {formatCurrency(bcDupla)} × {formatPercent(aliqInterna)} ={' '}
          {formatCurrency(icmsDestino)}
        </Text>
        <Text fontWeight="bold" mt={1}>
          5) DIFAL = {formatCurrency(icmsDestino)} − {formatCurrency(icmsOrigem)} ={' '}
          {formatCurrency(valorDifal)}
        </Text>
      </Box>
    );
  }

  const base = toNumber(nota.vcontabil);
  const diff = aliqInterna - aliqInter;
  return (
    <Box fontSize="11px" lineHeight="1.6">
      <Text fontWeight="bold" mb={1}>
        Base única
      </Text>
      <Text>
        {formatCurrency(base)} × ({formatPercent(aliqInterna)} − {formatPercent(aliqInter)}) ={' '}
        {formatCurrency(base * diff)}
      </Text>
    </Box>
  );
}

export function DifalNotasTable({ data, pagination, isFetching }: DifalNotasTableProps) {
  const columns = useMemo<ColumnDef<IDifalNota>[]>(
    () => [
      {
        header: 'Data',
        accessorKey: 'data_nota',
        cell: info => {
          const v = info.getValue() as string;
          return v ? moment(v).format('DD/MM/YYYY') : '—';
        },
      },
      {
        header: 'Nº / Série',
        accessorKey: 'numero_nota',
        cell: info => {
          const row = info.row.original;
          return (
            <Text fontSize="12px">
              {row.numero_nota}
              {row.serie ? ` / ${row.serie}` : ''}
            </Text>
          );
        },
      },
      {
        header: 'CFOP',
        accessorKey: 'cfop',
        cell: info => (info.getValue() as string | number)?.toString() || '—',
      },
      {
        header: 'Cliente',
        accessorKey: 'nome_clifor',
        cell: info => {
          const row = info.row.original;
          const pf = isPessoaFisica(row);
          return (
            <Box textAlign="left">
              <Text fontSize="12px" fontWeight="medium" noOfLines={1}>
                {row.nome_clifor || '—'}
              </Text>
              <Text fontSize="10px" color="gray.500">
                {row.cgce_clifor ? formatCPFOrCNPJ(row.cgce_clifor) : '—'}
                {' · '}
                {pf ? 'PF' : 'PJ'}
              </Text>
            </Box>
          );
        },
      },
      {
        header: 'UF',
        id: 'uf',
        cell: info => {
          const row = info.row.original;
          return (
            <Text fontSize="12px" fontWeight="medium">
              {row.uf_origem || '—'} → {row.uf_destino || '—'}
            </Text>
          );
        },
      },
      {
        header: 'Operação',
        id: 'operacao',
        cell: info => {
          const row = info.row.original;
          const isEntrada = row.tipo_difal === 'entrada';
          const isContrib = row.tipo_destinatario === 'contribuinte';
          return (
            <HStack spacing={1} justify="center">
              <Badge colorScheme={isEntrada ? 'blue' : 'green'} fontSize="9px">
                {isEntrada ? 'Entrada' : 'Saída'}
              </Badge>
              <Badge colorScheme={isContrib ? 'purple' : 'gray'} fontSize="9px">
                {isContrib ? 'Contrib.' : 'Não contrib.'}
              </Badge>
            </HStack>
          );
        },
      },
      {
        header: 'Fórmula',
        accessorKey: 'formula_aplicada',
        cell: info => {
          const v = info.getValue() as IDifalNota['formula_aplicada'];
          if (!v) return '—';
          const isDupla = v === 'base_dupla';
          return (
            <Badge
              colorScheme={isDupla ? 'red' : 'gray'}
              variant={isDupla ? 'solid' : 'subtle'}
              fontSize="9px"
            >
              {isDupla ? 'Base dupla' : 'Base única'}
            </Badge>
          );
        },
      },
      {
        header: 'Alíq. (interest. / interna)',
        id: 'aliquotas',
        cell: info => {
          const row = info.row.original;
          if (row.aliquota_interestadual === undefined && row.aliquota_interna === undefined) {
            return '—';
          }
          return (
            <Text fontSize="12px">
              {formatPercent(row.aliquota_interestadual)} / {formatPercent(row.aliquota_interna)}
            </Text>
          );
        },
      },
      {
        header: 'DIFAL',
        accessorKey: 'valor_difal_calculado',
        cell: info => {
          const row = info.row.original;
          return (
            <Tooltip
              hasArrow
              placement="left"
              bg="white"
              color="black"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              boxShadow="lg"
              p={3}
              label={<AuditTooltip nota={row} />}
            >
              <Text
                fontWeight="bold"
                color="#F57C00"
                cursor="help"
                textDecoration="underline"
                textDecorationStyle="dotted"
              >
                {formatCurrency(toNumber(info.getValue() as number | string))}
              </Text>
            </Tooltip>
          );
        },
      },
    ],
    [],
  );

  return (
    <DataTable<IDifalNota>
      columns={columns}
      data={data}
      pagination={pagination}
      isFetching={isFetching}
      emptyMsg="Nenhuma nota DIFAL no período."
      loadingMsg="Carregando notas DIFAL..."
    />
  );
}
